<template>
  <UmisisModal
    ref="customDeadlineModal"
    :ok-disabled="isUpdating || !isValidAmount"
    title="Set Custom Default Deadline"
    @ok="submitCustomDeadline"
    @show="onModalShow"
  >
    <div class="flex flex-col gap-4">
      <Field>
        <FieldLabel for="task-template-custom-deadline-amount">
          Deadline offset
        </FieldLabel>
        <div class="grid grid-cols-[1fr_auto] gap-2">
          <Input
            id="task-template-custom-deadline-amount"
            v-model="amountInput"
            min="1"
            type="number"
          />
          <select
            v-model="unit"
            class="flex h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <option value="minutes">
              Minutes
            </option>
            <option value="hours">
              Hours
            </option>
            <option value="days">
              Days
            </option>
            <option value="weeks">
              Weeks
            </option>
          </select>
        </div>
        <FieldDescription>
          This defines how far after launch the generated task deadline should be.
        </FieldDescription>
      </Field>
    </div>
  </UmisisModal>
</template>

<script setup>
import { computed, ref } from 'vue'
import { toast } from 'vue-sonner'
import UmisisModal from '../../../../../../../../resources/js/components/umisis/generic/UmisisModal.vue'
import { useModalControl } from '../../../../../../../../resources/js/composables/modal/useUmisisModal.js'
import { Field, FieldDescription, FieldLabel } from '../../../../../../../../resources/js/components/ui/field/index.js'
import { Input } from '../../../../../../../../resources/js/components/ui/input/index.js'
import { useTaskTemplateStore } from '../../../stores/taskTemplateStore.js'
import {
  formatTaskTemplateDeadlineLabel,
  normalizeDeadlineMinutes,
} from '../../../utils/formatTaskTemplateDeadline.js'

const UNIT_TO_MINUTES = {
  minutes: 1,
  hours: 60,
  days: 60 * 24,
  weeks: 60 * 24 * 7,
}

const props = defineProps({
  taskTemplateId: {
    type: Number,
    required: true,
  },
  value: {
    type: [Number, String, Object],
    default: null,
  },
  context: {
    type: String,
    default: 'task-template-show',
  },
})

const amountInput = ref('1')
const unit = ref('days')

const { componentRef: customDeadlineModal, openModal, closeModal } = useModalControl()

const taskTemplateStore = useTaskTemplateStore(props.context)
const normalizedMinutes = computed(() => normalizeDeadlineMinutes(props.value))
const isUpdating = computed(() => taskTemplateStore.isUpdatingDefaultDeadline(props.taskTemplateId))
const normalizedAmount = computed(() => {
  const parsedAmount = Number(amountInput.value)

  if (!Number.isInteger(parsedAmount) || parsedAmount < 1) {
    return null
  }

  return parsedAmount
})
const isValidAmount = computed(() => normalizedAmount.value !== null)

const normalizeInitialValues = (minutes) => {
  if (!Number.isInteger(minutes) || minutes < 1) {
    return {
      amount: 1,
      unit: 'days',
    }
  }

  if (minutes % UNIT_TO_MINUTES.weeks === 0) {
    return {
      amount: minutes / UNIT_TO_MINUTES.weeks,
      unit: 'weeks',
    }
  }

  if (minutes % UNIT_TO_MINUTES.days === 0) {
    return {
      amount: minutes / UNIT_TO_MINUTES.days,
      unit: 'days',
    }
  }

  if (minutes % UNIT_TO_MINUTES.hours === 0) {
    return {
      amount: minutes / UNIT_TO_MINUTES.hours,
      unit: 'hours',
    }
  }

  return {
    amount: minutes,
    unit: 'minutes',
  }
}

const formatForToast = (minutes) => {
  return formatTaskTemplateDeadlineLabel(minutes, 'after')
}

const onModalShow = () => {
  const initialValues = normalizeInitialValues(normalizedMinutes.value)
  amountInput.value = String(initialValues.amount)
  unit.value = initialValues.unit
}

const submitCustomDeadline = async (event) => {
  event?.preventDefault?.()

  if (!Number.isInteger(props.taskTemplateId) || props.taskTemplateId <= 0) {
    return
  }

  if (!isValidAmount.value) {
    toast.error('Please provide a valid amount.')
    return
  }

  const multiplier = UNIT_TO_MINUTES[unit.value] ?? 1
  const resolvedMinutes = (normalizedAmount.value ?? 0) * multiplier

  if (!Number.isInteger(resolvedMinutes) || resolvedMinutes < 1) {
    toast.error('Please provide a valid default deadline.')
    return
  }

  try {
    await taskTemplateStore.updateDefaultDeadline(props.taskTemplateId, resolvedMinutes)
    const formatted = formatForToast(resolvedMinutes)
    toast.success(formatted ? `Default deadline set ${formatted}.` : 'Default deadline updated.')
    closeModal()
  } catch (error) {
    const errorMessage = error?.response?.data?.message
      || error?.response?.data?.errors?.default_deadline_after_minutes?.[0]
      || 'Unable to update the default deadline.'

    toast.error(errorMessage)
  }
}

defineExpose({
  openModal,
})
</script>
